import { useState } from 'react';
import ReactSelectComponent from 'react-select';
import { useTranslation } from 'react-i18next';
import FormLabel from '@material-ui/core/FormLabel';
import { FormatListBulleted, ListAlt } from '@material-ui/icons';
import { Divider } from '@material-ui/core';
import makeStyles from '@material-ui/styles/makeStyles';
import {
  grayColor,
  warningColor,
  dangerColor,
  infoColor,
  whiteColor,
} from 'assets/jss/material-dashboard-pro-react';
import Card from 'components/Card/Card';
import LogView from 'components/History/LogView';
import DataGrid from 'components/DataGrid/DataGrid';
import CardBody from 'components/Card/CardBody';
import GridItem from 'components/Grid/GridItem';
import ReactSelect from 'components/ReactSelect/ReactSelect';
import SelectInput from 'components/FormSelect/SelectInput';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import GridContainer from 'components/Grid/GridContainer';
import FormImageUpload from 'components/FormImageUpload/FormImageUpload';
import FormSelectDropdown from 'components/FormSelect/FormSelectDropdown';
import FormTextInputTranslatable from 'components/FormTextInput/FormTextInputTranslatable';
import { DialogLoader } from 'components/DialogLoader';
import { mapArrayOfTranslationsToPureArray } from 'components/Translatable/TranslatableReader';
import defaultImageForClient from 'assets/img/dishPhotoForClientTemplate.jpg';
import {
  isGranted,
  sortByPosition,
  sortByGroupedBrandPosition,
} from 'helpers/helpers';
import roles from 'helpers/roles';
import FormControlStickyButton from 'components/FormControlStickyButton/FormControlStickyButton';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';

import TypeBlock from './TypeBlockComponents/TypeBlock';
import CopyMealToMeals from './CopyMealToMeals';
import DishIngredients from './DishIngredients';
import menuPlannerColumnConfig from './menuPlannerColumnConfig';
import tagSelectStyles from './tagSelectStyles';
import ShopSaleSection from './ShopSale/ShopSaleSection';

const useStyles = makeStyles({
  section: {
    '& label': {
      color: grayColor[1],
    },
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: '16px',
  },
  sectionDivider: {
    marginBottom: '24px',
  },
  sectionFields: {
    '& > div': {
      marginTop: 0,
      marginBottom: '24px',
    },
    '& > div:last-child': {
      marginBottom: 0,
    },
  },
  sectionBody: {
    padding: '16px 20px',
  },
  chipList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  chip: {
    display: 'inline-block',
    padding: '8px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '1.2',
    textTransform: 'uppercase',
  },
  chipNeutral: {
    backgroundColor: grayColor[8],
    color: grayColor[2],
  },
  chipDanger: {
    backgroundColor: dangerColor[0],
    color: whiteColor,
  },
  imageUpload: {
    '& > div': {
      marginTop: 0,
      marginBottom: 0,
    },
    '& > div:first-child': {
      marginBottom: '8px',
      '& > div': {
        marginLeft: 0,
      },
    },
  },
  balancingCard: {
    marginTop: '40px',
  },
  brandTabs: {
    position: 'sticky',
    top: '20px',
    zIndex: 2,
    display: 'inline-flex',
    flexWrap: 'wrap',
    borderRadius: '3px',
    backgroundColor: grayColor[8],
    marginBottom: '16px',
  },
  brandTab: {
    padding: '8px 20px',
    border: 'none',
    borderRadius: '5px',
    backgroundColor: 'transparent',
    color: grayColor[1],
    fontFamily: 'inherit',
    fontSize: '20px',
    fontWeight: 400,
    lineHeight: '1.25',
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  brandTabActive: {
    backgroundColor: infoColor[0],
    color: whiteColor,
  },
  per100gInfo: {
    margin: '0 0 8px',
    color: warningColor[0],
    fontSize: '12px',
    fontWeight: 400,
    textAlign: 'left',
  },
  sectionRow: {
    '& > div': {
      marginTop: 0,
      marginBottom: '24px',
    },
  },
  copySection: {
    '& label': {
      color: grayColor[1],
    },
  },
});

const BasicMealForm = ({
  dish,
  tags,
  match,
  brands,
  classes,
  history,
  addMeal,
  getImage,
  copyMode,
  allergens,
  mealTypes,
  isLoading,
  isEditing,
  kwgGroups,
  clientTags,
  containers,
  handleBrand,
  removeImage,
  getStarRate,
  handleSelect,
  handleChange,
  handleRemove,
  isSubmitting,
  handleSubmit,
  shouldRescale,
  scaleDishSize,
  ecoContainers,
  handleMealType,
  clientImageUrl,
  copyMealTypeTo,
  handleCopySize,
  setDishToResize,
  handleTagChange,
  copyMealTypeFrom,
  setShouldRescale,
  handlePriceChange,
  servingSuggestions,
  productionImageUrl,
  availableInVariableOffer,
  getFilteredRecipes,
  dishCategories = [],
  showValidationAlert,
  checkedDishToResize,
  selectedIngredients,
  selectedKwgGroupIri,
  handleCategoryChange,
  handleCopySizeChange,
  handleSizeNameChange,
  handleQuantityChange,
  handleContainerChange,
  getFilteredIngredients,
  mealTypeIngredientsSum,
  isAvailableInFixedOffer,
  handleEcoContainerChange,
  handleSelectedKwgGroupIriChange,
  handleSizeFixedOfferChange,
  handleChangeAvailableInFixedOffer,
  handleBrandFixedOfferChange,
  handleChangeAvailableInVariableOffer,
  handleChangeExcludedFromDiscountCodes,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [mealTypeInputValue, setMealTypeInputValue] = useState('');
  const [activeBrandIndex, setActiveBrandIndex] = useState(0);

  const groupedTypes = dish.types.reduce(
    (acc, { brand, ...itemWithoutBrand }) => {
      const { '@id': brandId } = brand;

      return {
        ...acc,
        [brandId]: {
          ...brand,
          types:
            acc[brandId] === undefined
              ? [itemWithoutBrand]
              : [...acc[brandId].types, itemWithoutBrand],
        },
      };
    },
    {}
  );

  const groupedTypesArray = Object.values(groupedTypes);

  const selectedBrandMealTypes = Object.values(groupedTypes).reduce(
    (acc, { types, name }) => {
      const typesWithBrand = types.map(type => ({
        ...type,
        brandName: name,
      }));

      return [...acc, ...typesWithBrand];
    },
    []
  );

  const isMealTypesDisabled = dish.brands.length === 0;

  const mapMealTypesToReactSelectOption = options =>
    options.map(option => ({
      label:
        option.brand && option.brand.name
          ? `${option.name} - ${option.brand.name}`
          : option.name,
      value: option,
    }));

  const sortByDate = collection =>
    [...collection].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

  const selectedKwgGroup = kwgGroups.find(
    kwgGroup => kwgGroup['@id'] === selectedKwgGroupIri
  );

  const activeBrandId = Object.values(groupedTypes)
    [activeBrandIndex]?.['@id'].split('/')
    .pop();

  const allergenNames = [
    ...new Set(mapArrayOfTranslationsToPureArray(allergens)),
  ];
  const hasAllergens = allergenNames.length > 0;

  return (
    <div>
      <DialogLoader
        loaderState={isSubmitting}
        text={t('form.savingChanges', 'Zapisywanie zmian...')}
      />
      <form>
        <Card className={styles.section}>
          <CardBody className={styles.sectionBody}>
            <GridContainer>
              <GridItem md={6}>
                <h3 className={styles.sectionTitle}>{t('form.basicInfo')}</h3>
                <Divider className={styles.sectionDivider} />
                <GridContainer className={styles.sectionFields}>
                  <GridItem md={12}>
                    <FormLabel style={{ fontSize: '14px' }}>
                      {`${t('form.field.defaultName')}*`}
                    </FormLabel>
                    <FormTextInputTranslatable
                      data-cy="dish__name"
                      noGrid={true}
                      name="nameForClient"
                      value={dish.nameForClient}
                      handleChange={handleChange}
                      maxLength={160}
                    />
                  </GridItem>
                  <GridItem md={12}>
                    <FormLabel style={{ fontSize: '14px' }}>
                      {`${t('form.field.workingName')}*`}
                    </FormLabel>
                    <FormTextInput
                      data-cy="dish__working-name"
                      noGrid={true}
                      classes={classes}
                      name="workingName"
                      value={dish.workingName}
                      handleChange={handleChange}
                      maxLength={160}
                    />
                  </GridItem>
                  <GridItem md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {`${t('form.field.brands')} *`}
                    </FormLabel>
                    <SelectInput
                      multiple={true}
                      noGrid={true}
                      classes={classes}
                      mapBy="name"
                      trackBy="@id"
                      options={brands}
                      value={dish.brands}
                      handleChange={handleBrand}
                      name="brands"
                    />
                  </GridItem>
                  <GridItem
                    md={12}
                    onClick={() =>
                      isMealTypesDisabled &&
                      showValidationAlert(t('mealTypes.select.warning'))
                    }
                  >
                    <FormLabel
                      disabled={isMealTypesDisabled}
                      className={classes.labelHorizontal}
                    >
                      {`${t('form.field.mealTypes')}*`}
                    </FormLabel>

                    <ReactSelect
                      closeMenuOnSelect={false}
                      inputValue={mealTypeInputValue}
                      onInputChange={(value, action) => {
                        if (action.action === 'input-change') {
                          setMealTypeInputValue(value);
                        }
                      }}
                      selectedValues={mapMealTypesToReactSelectOption(
                        dish.types
                      )}
                      options={mapMealTypesToReactSelectOption(
                        sortByGroupedBrandPosition(mealTypes).filter(
                          item =>
                            mapMealTypesToReactSelectOption(
                              dish.types
                            ).findIndex(
                              ({ value }) => value['@id'] === item['@id']
                            ) === -1
                        )
                      )}
                      handleChange={(obj, option) => {
                        if (option.action === 'clear') {
                          setMealTypeInputValue('');
                        }

                        if (option.action !== 'pop-value') {
                          handleMealType(obj.map(({ value }) => value));
                        }
                      }}
                      filterOption={(option, searchText) =>
                        option.label
                          .toLowerCase()
                          .includes(searchText.toLowerCase())
                      }
                      placeholder=" "
                      noOptionsMessage={t(
                        '$*noSearchResults',
                        'Brak wyników wyszukiwania'
                      )}
                      Component={ReactSelectComponent}
                      customStyles={tagSelectStyles}
                      disabled={isMealTypesDisabled}
                    />
                  </GridItem>
                  <GridItem md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('form.field.servingProposal')}
                    </FormLabel>
                    <SelectInput
                      noGrid={true}
                      classes={classes}
                      options={servingSuggestions}
                      value={dish.servingSuggestion}
                      name="servingSuggestion"
                      mapBy="value"
                      trackBy="@id"
                      handleChange={handleSelect}
                    />
                  </GridItem>
                  <GridItem md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('form.field.tagsForClient')}
                    </FormLabel>
                    <SelectInput
                      multiple={true}
                      noGrid={true}
                      classes={classes}
                      label={t('form.field.tagsForClient')}
                      options={clientTags}
                      value={dish.clientTags}
                      name="clientTags"
                      mapBy="value"
                      trackBy="@id"
                      handleChange={handleChange}
                    />
                  </GridItem>
                  {kwgGroups.length > 0 && (
                    <GridItem md={12}>
                      <FormLabel className={classes.labelHorizontal}>
                        {t('form.field.kwgGroup')}
                      </FormLabel>
                      <ReactSelect
                        closeMenuOnSelect={true}
                        value={
                          selectedKwgGroup
                            ? {
                                value: selectedKwgGroup['@id'],
                                label: selectedKwgGroup.name,
                              }
                            : null
                        }
                        options={kwgGroups.map(kwgGroup => ({
                          value: kwgGroup['@id'],
                          label: kwgGroup.name,
                        }))}
                        handleChange={selectedElement => {
                          handleSelectedKwgGroupIriChange(
                            selectedElement?.value || ''
                          );
                        }}
                        noOptionsMessage={t(
                          '$*noSearchResults',
                          'Brak wyników wyszukiwania'
                        )}
                        Component={ReactSelectComponent}
                        name="selectedKwgGroup"
                        isMulti={false}
                        isClearable={true}
                      />
                    </GridItem>
                  )}
                  <GridItem md={12}>
                    <FormTextInput
                      data-cy="dish__description"
                      noGrid={true}
                      label={t('form.field.description')}
                      classes={classes}
                      name="description"
                      value={dish.description}
                      handleChange={handleChange}
                      inputSize={12}
                      multiline={true}
                      rows={1}
                      rowsMax={10}
                      maxLength={500}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridItem md={6}>
                <GridContainer>
                  <GridItem md={6} className={styles.imageUpload}>
                    <FormImageUpload
                      label={t('form.field.photoForClient')}
                      labelAdditionalContent={
                        <InfoIconTooltip
                          title={t('form.field.photoForClientTooltip')}
                        />
                      }
                      stateName="clientImage"
                      getImage={getImage}
                      removeImage={removeImage}
                      defaultImage={defaultImageForClient}
                      previewUrl={clientImageUrl}
                    />
                  </GridItem>
                  {isEditing && (
                    <GridItem md={12}>
                      <FormLabel style={{ fontSize: '14px' }}>
                        {t('form.field.avgRate')}
                      </FormLabel>
                      <h4>
                        <b>{dish.rateAVG ? dish.rateAVG.toFixed(2) : '0'}</b>{' '}
                        {t('form.field.basedOn')} {dish.rateCount || 0}{' '}
                        {t('form.field.rates')}
                      </h4>
                      {getStarRate(dish.rateAVG)}
                    </GridItem>
                  )}
                </GridContainer>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>

        <Card className={styles.section}>
          <CardBody className={styles.sectionBody}>
            <GridContainer>
              <GridItem md={6}>
                <h3 className={styles.sectionTitle}>
                  {t('form.field.productionInfo')}
                </h3>
                <Divider className={styles.sectionDivider} />
                <GridContainer className={styles.sectionFields}>
                  <GridItem md={12}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('form.field.tagsForProduction')}
                    </FormLabel>
                    <ReactSelect
                      selectedValues={dish.tags}
                      options={tags}
                      handleChange={handleTagChange}
                      customStyles={tagSelectStyles}
                    />
                  </GridItem>
                  <GridItem md={12}>
                    <FormTextInput
                      data-cy="dish__preparation-instruction"
                      noGrid={true}
                      label={t('form.field.preparationInstruction')}
                      tooltip={t('form.field.preparationInstructionTooltip')}
                      classes={classes}
                      name="preparingInstructions"
                      value={dish.preparingInstructions}
                      handleChange={handleChange}
                      inputSize={12}
                      multiline={true}
                      rows={1}
                      rowsMax={20}
                      maxLength={5000}
                    />
                  </GridItem>
                  <GridItem md={12}>
                    <FormTextInput
                      data-cy="dish__notes"
                      noGrid={true}
                      label={t('form.field.notes')}
                      classes={classes}
                      name="note"
                      value={dish.note}
                      handleChange={handleChange}
                      inputSize={12}
                      multiline={true}
                      rows={1}
                      rowsMax={20}
                      maxLength={5000}
                    />
                  </GridItem>
                  <GridItem md={12}>
                    <FormTextInput
                      noGrid={true}
                      label={t('form.field.additionalNotesToSticker')}
                      classes={classes}
                      name="additionalNotesToSticker"
                      value={dish.additionalNotesToSticker}
                      handleChange={handleChange}
                      inputSize={12}
                      multiline={true}
                      rows={1}
                      rowsMax={20}
                      maxLength={5000}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridItem md={6}>
                <GridContainer>
                  <GridItem md={6} className={styles.imageUpload}>
                    <FormImageUpload
                      label={t('form.field.photoForProduction')}
                      labelAdditionalContent={
                        <InfoIconTooltip title={t('form.field.photoSizes')} />
                      }
                      stateName="productionImage"
                      getImage={getImage}
                      removeImage={removeImage}
                      previewUrl={productionImageUrl}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </form>

      {isGranted(roles.ROLE_SHOW_MENU_PLANNING) &&
        isEditing &&
        (dish?.brands ?? []).length > 0 && (
          <Card className={styles.section}>
            <CardBody className={styles.sectionBody}>
              <h3 className={styles.sectionTitle}>
                {t('form.field.dishAppearedOnMenuPlanning')}
              </h3>
              <Divider className={styles.sectionDivider} />
              <GridContainer className={styles.sectionRow}>
                <GridItem md={12}>
                  <DataGrid
                    disableTotal={true}
                    defaultSorted={[{ id: 'date', desc: true }]}
                    filterable={true}
                    sortable={false}
                    paginationTop={true}
                    paginationBottom={false}
                    url="/menu-planners"
                    columns={menuPlannerColumnConfig}
                    role="MENU_PLANNER"
                    minRows={5}
                    customDefaultPageSize={5}
                    showPageSizeOptions={false}
                    manipulateQuery={(requestData, query) => {
                      query.dish = match.params.id;
                      query.selectInBrands = dish.brands.map(
                        brand => brand['@id']
                      );

                      query.properties = [
                        'date',
                        'dish',
                        'brand',
                        'variant',
                        'draft',
                      ];
                      query['properties[diet]'] = ['name'];
                      query['properties[brand]'] = ['name'];
                      query['properties[variant]'] = ['name'];

                      return query;
                    }}
                  />
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        )}

      <Card className={styles.section}>
        <CardBody className={styles.sectionBody}>
          <h3 className={styles.sectionTitle}>
            {t('form.field.dishIngredients')}
          </h3>
          <Divider className={styles.sectionDivider} />
          <GridContainer className={styles.sectionRow}>
            <GridItem md={6}>
              <FormSelectDropdown
                includeId={true}
                filterName="workName"
                placeholder={
                  <div style={{ display: 'flex' }}>
                    <FormatListBulleted
                      style={{ marginRight: '5px' }}
                      fontSize="small"
                    />
                    <span>{t('form.field.addIngredient')}</span>
                  </div>
                }
                handleClick={addMeal}
                clearAfterSelect={true}
                filter={getFilteredIngredients}
              />
            </GridItem>
            <GridItem md={6}>
              <FormSelectDropdown
                includeId={true}
                filterName="workName"
                placeholder={
                  <div style={{ display: 'flex' }}>
                    <ListAlt style={{ marginRight: '5px' }} fontSize="small" />
                    <span>{t('form.field.addRecipe')}</span>
                  </div>
                }
                handleClick={addMeal}
                clearAfterSelect={true}
                filter={getFilteredRecipes}
              />
            </GridItem>
          </GridContainer>
          <GridContainer className={styles.sectionRow}>
            <GridItem md={12}>
              <div style={{ marginBottom: '8px' }}>
                <FormLabel style={{ fontSize: '14px' }}>
                  {t('form.field.allergens')}
                </FormLabel>
              </div>
              <div className={styles.chipList}>
                {hasAllergens ? (
                  allergenNames.map(allergenName => (
                    <span
                      key={allergenName}
                      className={`${styles.chip} ${styles.chipDanger}`}
                    >
                      {allergenName}
                    </span>
                  ))
                ) : (
                  <span className={`${styles.chip} ${styles.chipNeutral}`}>
                    {t('form.field.noAllergens')}
                  </span>
                )}
              </div>
            </GridItem>
          </GridContainer>
          <GridContainer className={styles.sectionRow}>
            <GridItem md={12}>
              <p className={styles.per100gInfo}>
                {t('form.field.100gramInfo')}
              </p>
              <DishIngredients
                data={selectedIngredients}
                history={history}
                handleRemove={handleRemove}
              />
            </GridItem>
          </GridContainer>
        </CardBody>
      </Card>

      <Card>
        <CardBody className={styles.sectionBody}>
          <h3 className={styles.sectionTitle}>
            {t('mealTypes.dishSizeConfiguration')}
          </h3>
          <Divider className={styles.sectionDivider} />
          <div className={styles.copySection}>
            <CopyMealToMeals
              copyMealTypeFrom={copyMealTypeFrom}
              copyMealTypeTo={copyMealTypeTo}
              handleCopySize={handleCopySize}
              handleCopySizeChange={handleCopySizeChange}
              selectedBrandMealTypes={selectedBrandMealTypes}
            />
          </div>

          <Card className={styles.balancingCard}>
            <CardBody className={styles.sectionBody}>
              {groupedTypesArray.length > 0 && (
                <div className={styles.brandTabs}>
                  {groupedTypesArray.map((types, key) => (
                    <button
                      key={types['@id']}
                      type="button"
                      className={`${styles.brandTab} ${
                        key === activeBrandIndex ? styles.brandTabActive : ''
                      }`}
                      onClick={() => setActiveBrandIndex(key)}
                    >
                      {types.name}
                    </button>
                  ))}
                </div>
              )}
              {/* eslint-disable-next-line no-negated-condition -- FIXME */}
              {!groupedTypesArray.length ? (
                <GridContainer>
                  <GridItem md={12}>
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '20px',
                      }}
                    >
                      <h1>
                        {t(
                          'mealTypes.chooseMealType',
                          'Wybierz typy posiłków, aby konfigurować dania'
                        )}
                      </h1>
                    </div>
                  </GridItem>
                </GridContainer>
              ) : (
                <GridContainer>
                  <GridItem md={12}>
                    {sortByPosition(
                      groupedTypesArray[activeBrandIndex].types
                    ).map(type => {
                      const blockId = `${groupedTypesArray[activeBrandIndex]?.[
                        '@id'
                      ].replace('/brands/', '')}_${type.id}`;

                      return (
                        <TypeBlock
                          classes={classes}
                          key={blockId}
                          blockId={blockId}
                          type={type}
                          dish={dish}
                          dishSizes={dish.sizes}
                          selectedIngredients={selectedIngredients}
                          mealTypeIngredientsSum={mealTypeIngredientsSum}
                          onChange={handleQuantityChange}
                          containers={sortByDate(containers)}
                          ecoContainers={sortByDate(ecoContainers)}
                          handleContainerChange={handleContainerChange}
                          handleEcoContainerChange={handleEcoContainerChange}
                          handleSizeNameChange={handleSizeNameChange}
                          scaleDishSize={scaleDishSize}
                          setShouldRescale={setShouldRescale}
                          shouldRescale={shouldRescale}
                          checkedDishToResize={checkedDishToResize}
                          setDishToResize={setDishToResize}
                          selectedBrandMealTypes={selectedBrandMealTypes}
                          handleCopySizeChange={handleCopySizeChange}
                          handleCopySize={handleCopySize}
                          activeBrandId={activeBrandId}
                          copyMode={copyMode}
                          isEditing={isEditing}
                        />
                      );
                    })}
                  </GridItem>
                </GridContainer>
              )}
            </CardBody>
          </Card>
        </CardBody>
      </Card>

      <ShopSaleSection
        dish={dish}
        groupedTypes={groupedTypesArray}
        dishCategories={dishCategories}
        handleCategoryChange={handleCategoryChange}
        handleChange={handleChange}
        isAvailableInVariableOffer={availableInVariableOffer}
        isAvailableInFixedOffer={isAvailableInFixedOffer}
        handleChangeAvailableInVariableOffer={
          handleChangeAvailableInVariableOffer
        }
        handleChangeAvailableInFixedOffer={handleChangeAvailableInFixedOffer}
        isExcludedFromDiscountCodes={!!dish.excludedFromDiscountCodes}
        handleChangeExcludedFromDiscountCodes={
          handleChangeExcludedFromDiscountCodes
        }
        handleSizeFixedOfferChange={handleSizeFixedOfferChange}
        handleBrandFixedOfferChange={handleBrandFixedOfferChange}
        handlePriceChange={handlePriceChange}
      />
      {!isLoading && !isEditing && (
        <FormControlStickyButton
          t={t}
          classes={classes}
          discardText={t('form.cancel')}
          submitText={t('form.saveAndClose')}
          cancelPath="/admin/meals"
          secondarySubmitText={t('form.save')}
          handleSubmit={e => handleSubmit(e, true)}
          handleSecondarySubmit={e => handleSubmit(e, false)}
        />
      )}
      {isEditing && (
        <LogView
          classes={classes}
          discardText={t('form.cancel')}
          submitText={t('form.saveAndClose')}
          cancelPath="/admin/meals"
          secondarySubmitText={t('form.save')}
          handleSubmit={e => handleSubmit(e, true)}
          handleSecondarySubmit={e => handleSubmit(e, false)}
          iri={`/dishes/${match.params.id}`}
        />
      )}
    </div>
  );
};

export default BasicMealForm;
